package org.zq.astrock_ai.quant

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min

/**
 * 影响买卖区的资金/公告上下文（研究参考）。
 * 金额单位：元。
 */
data class ZoneMarketContext(
    val todayMainNet: Double? = null,
    val recentMainNet: Double? = null,
    /** 近期公告/新闻标题，用于风险关键词打分 */
    val headlines: List<String> = emptyList(),
    /** 昨收（翻红线）；反抽/早盘翻红玩法用于收紧卖区 */
    val preClose: Double? = null,
    /** 分时日内高 */
    val dayHigh: Double? = null,
    /** 分时均价 */
    val vwap: Double? = null,
    /** 深跌反抽或早盘深跌翻红：卖区优先锚定昨收～微红 */
    val reboundSellMode: Boolean = false,
)

data class ZoneMarketAdjust(
    /** 卖区中枢相对位移，正=抬高（资金偏多），负=压低（兑现更早） */
    val sellShiftPct: Double = 0.0,
    /** 买区中枢相对位移，正=抬高，负=压低等待更便宜 */
    val buyShiftPct: Double = 0.0,
    /** 0~0.4，卖区收窄（风险时更贴近现价） */
    val sellTighten: Double = 0.0,
    /** 0~0.4，买区下沿放宽（风险/流出时更深承接） */
    val buyWidenDown: Double = 0.0,
    val confidenceDelta: Double = 0.0,
    val reasons: List<String> = emptyList(),
    val flowScore: Double = 0.0,
    val riskScore: Double = 0.0,
)

fun evaluateZoneMarketAdjust(ctx: ZoneMarketContext?): ZoneMarketAdjust {
    if (ctx == null) return ZoneMarketAdjust()
    val hasFlow = ctx.todayMainNet != null || ctx.recentMainNet != null
    val hasNews = ctx.headlines.any { it.isNotBlank() }
    if (!hasFlow && !hasNews) return ZoneMarketAdjust()

    val flow = scoreMainFlow(ctx.todayMainNet, ctx.recentMainNet)
    val (risk, newsReasons) = scoreAnnouncementRisk(ctx.headlines)
    val riskUp = risk.coerceAtLeast(0.0)
    val bull = (-risk).coerceAtLeast(0.0)

    val sellShift = (flow * 0.012 - riskUp * 0.018 + bull * 0.006).coerceIn(-0.035, 0.03)
    val buyShift = (flow * 0.008 - riskUp * 0.016 + bull * 0.004).coerceIn(-0.035, 0.025)
    val sellTighten = (riskUp * 0.22 + (if (flow < -0.35) 0.08 else 0.0)).coerceIn(0.0, 0.4)
    val buyWiden = (riskUp * 0.18 + (if (flow < -0.35) 0.10 else 0.0)).coerceIn(0.0, 0.4)
    val confDelta = (
        (if (abs(flow) >= 0.35) 0.04 else 0.0) +
            (if (riskUp >= 0.35) -0.06 else 0.0) +
            (if (bull >= 0.25) 0.03 else 0.0)
        ).coerceIn(-0.12, 0.08)

    val reasons = buildList {
        if (hasFlow) {
            val today = ctx.todayMainNet
            val recent = ctx.recentMainNet
            add(
                "主力资金：" +
                    "今日 ${formatSignedYi(today)}" +
                    (if (recent != null) "，近5日 ${formatSignedYi(recent)}" else "") +
                    when {
                        flow >= 0.45 -> "，偏多→卖区可略抬、买区不必过深"
                        flow <= -0.45 -> "，偏空→卖区更贴近现价、买区下移"
                        else -> "，中性微调"
                    },
            )
        }
        addAll(newsReasons)
    }

    return ZoneMarketAdjust(
        sellShiftPct = sellShift,
        buyShiftPct = buyShift,
        sellTighten = sellTighten,
        buyWidenDown = buyWiden,
        confidenceDelta = confDelta,
        reasons = reasons,
        flowScore = flow,
        riskScore = risk,
    )
}

internal fun applySellMarketAdjust(
    low: Double,
    high: Double,
    price: Double,
    adj: ZoneMarketAdjust,
): Pair<Double, Double> {
    if (low <= 0 || high <= 0 || high < low) return low to high
    if (adj.sellShiftPct == 0.0 && adj.sellTighten == 0.0) return low to high
    val mid = (low + high) / 2.0
    val half = (high - low) / 2.0
    val newMid = mid * (1.0 + adj.sellShiftPct)
    val tightHalf = half * (1.0 - adj.sellTighten)
    var bandLow = newMid - tightHalf
    var bandHigh = newMid + tightHalf
    if (adj.sellShiftPct < 0 || adj.sellTighten > 0) {
        val pull = ((-adj.sellShiftPct).coerceAtLeast(0.0) * 8.0 + adj.sellTighten).coerceIn(0.0, 0.55)
        val nearLow = max(price * 1.003, price + (mid - price) * 0.25)
        val nearHigh = max(nearLow * 1.006, price + (high - price) * 0.55)
        bandLow = bandLow * (1 - pull) + nearLow * pull
        bandHigh = bandHigh * (1 - pull) + nearHigh * pull
    }
    bandLow = max(price * 1.002, bandLow)
    bandHigh = max(bandLow * 1.006, bandHigh)
    return round2(bandLow) to round2(bandHigh)
}

internal fun applyBuyMarketAdjust(
    low: Double,
    high: Double,
    price: Double,
    adj: ZoneMarketAdjust,
): Pair<Double, Double> {
    if (low <= 0 || high <= 0 || high < low) return low to high
    if (adj.buyShiftPct == 0.0 && adj.buyWidenDown == 0.0) return low to high
    val mid = (low + high) / 2.0
    val half = (high - low) / 2.0
    val newMid = mid * (1.0 + adj.buyShiftPct)
    var bandHigh = min(price * 0.998, newMid + half)
    var bandLow = newMid - half * (1.0 + adj.buyWidenDown)
    bandLow = bandLow.coerceAtLeast(price * 0.82)
    bandHigh = min(price * 0.999, max(bandLow * 1.004, bandHigh))
    if (bandHigh < bandLow) return low to high
    return round2(bandLow) to round2(bandHigh)
}

private fun scoreMainFlow(today: Double?, recent: Double?): Double {
    var score = 0.0
    today?.let {
        score += when {
            it >= 200_000_000 -> 0.85
            it >= 80_000_000 -> 0.60
            it >= 30_000_000 -> 0.35
            it >= 8_000_000 -> 0.18
            it <= -200_000_000 -> -0.85
            it <= -80_000_000 -> -0.60
            it <= -30_000_000 -> -0.35
            it <= -8_000_000 -> -0.18
            else -> it / 200_000_000.0 * 0.15
        }
    }
    recent?.let {
        score += when {
            it >= 400_000_000 -> 0.20
            it >= 150_000_000 -> 0.12
            it <= -400_000_000 -> -0.20
            it <= -150_000_000 -> -0.12
            else -> 0.0
        }
    }
    return score.coerceIn(-1.0, 1.0)
}

private fun scoreAnnouncementRisk(headlines: List<String>): Pair<Double, List<String>> {
    val titles = headlines.map { it.trim() }.filter { it.isNotEmpty() }.take(16)
    if (titles.isEmpty()) return 0.0 to emptyList()

    val riskKw = listOf(
        "立案", "调查", "减持", "预亏", "亏损", "风险警示", "*ST", "退市",
        "问询", "关注函", "处罚", "违规", "冻结", "诉讼", "仲裁", "终止上市",
        "中止", "减值", "商誉", "被执行", "失信", "监管措施", "质押", "违约",
    )
    val bullKw = listOf(
        "回购", "增持", "中标", "预增", "扭亏", "业绩预盈", "超预期",
        "签约", "获得", "突破", "中标候选人", "股权激励",
    )

    var riskHits = 0
    var bullHits = 0
    val hitRisk = mutableListOf<String>()
    val hitBull = mutableListOf<String>()
    titles.forEach { title ->
        val rk = riskKw.firstOrNull { title.contains(it) }
        val bk = bullKw.firstOrNull { title.contains(it) }
        if (rk != null) {
            riskHits++
            if (hitRisk.size < 2) hitRisk += title.take(28)
        } else if (bk != null) {
            bullHits++
            if (hitBull.size < 2) hitBull += title.take(28)
        }
    }
    val score = (riskHits * 0.28 - bullHits * 0.18).coerceIn(-0.55, 1.0)
    val reasons = buildList {
        if (riskHits > 0) {
            add("公告风险关键词×$riskHits：${hitRisk.joinToString("；")}→卖区下移/收窄，买区更深")
        }
        if (bullHits > 0 && riskHits == 0) {
            add("公告偏正面关键词×$bullHits：${hitBull.joinToString("；")}→买卖区略抬")
        }
        if (riskHits == 0 && bullHits == 0) {
            add("近期公告未见显著风险/利好关键词，买卖区以技术锚点为主")
        }
    }
    return score to reasons
}

private fun formatSignedYi(value: Double?): String {
    if (value == null) return "--"
    val yi = value / 100_000_000.0
    val body = ((abs(yi) * 100.0).toInt() / 100.0).toString()
    return when {
        value > 0 -> "+${body}亿"
        value < 0 -> "-${body}亿"
        else -> "0亿"
    }
}

private fun round2(v: Double): Double = (v * 100.0).toInt() / 100.0
